/*
 * Os botões do fim do tópico:
 * pílulas com borda, ícone e texto — Compartilhar, Favorito, Sinalizar,
 * Marcar não lido — e "Responder" em destaque; o "…" com o que sobra dos
 * controles do tópico (renomear, apagar, imprimir…). Na linha de baixo, o
 * controle de notificações e o texto do nível ("Você será notificado(a)…").
 * O visitante só tem o "Responder".
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import classList from 'flarum/common/utils/classList';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import TopicNotifications, { notificationReason } from './TopicNotifications';
import { canSubscribe } from '../ui';
import { forumBool, isExtensionEnabled, t } from '../../utils/settings';
import { openShare } from '../../gaps/topic/share';
import { bookmarksEnabled } from '../../features/bookmarks/actions';
import { toggleTopicBookmark, topicIsBookmarked } from '../../features/bookmarks/index';
/** O primeiro post, se já está no store (o fim do tópico quase sempre o tem). */
function firstPost(discussion) {
  const linkedId = discussion.data.relationships?.firstPost?.data?.id;
  return linkedId && app.store.getById('posts', linkedId) || app.store.all('posts').find(post => post.discussion() === discussion && post.number() === 1) || null;
}
export default class TopicFooter extends Component {
  markingUnread = false;
  view() {
    const discussion = this.attrs.discussion;

    // O visitante só tem o "Responder", à direita (que abre a janela de entrar).
    if (!app.session.user) {
      return m("div", {
        className: "CanelaTopicFooter CanelaTopicFooter--guest",
        role: "group",
        "aria-label": t('topic.footer.aria_label')
      }, m("div", {
        className: "CanelaTopicFooter-main"
      }, this.replyButton(discussion)));
    }

    // O que já tem botão próprio não se repete no "…".
    const controls = DiscussionControls.controls(discussion, this.attrs.page);
    ['reply', 'canelaBookmark'].forEach(key => controls.has(key) && controls.remove(key));
    const controlItems = controls.toArray().filter(item => item && item.itemName && !/Separator$/.test(item.itemName));
    return m("div", {
      className: "CanelaTopicFooter",
      role: "group",
      "aria-label": t('topic.footer.aria_label')
    }, m("div", {
      className: "CanelaTopicFooter-main"
    }, m(Button, {
      className: "Button CanelaTopicFooter-button CanelaTopicFooter-share",
      icon: "fas fa-arrow-up-from-bracket",
      onclick: () => openShare(discussion)
    }, t('topic.footer.share')), this.bookmarkButton(discussion), this.flagButton(discussion), this.unreadButton(discussion), controlItems.length > 0 && m(Dropdown, {
      className: "CanelaTopicFooter-more",
      buttonClassName: "Button Button--icon CanelaTopicFooter-button",
      menuClassName: "Dropdown-menu--right",
      icon: "fas fa-ellipsis-h",
      caretIcon: "",
      label: "",
      helperText: null,
      accessibleToggleLabel: t('topic.footer.more')
    }, controls.toArray()), this.replyButton(discussion)), canSubscribe() &&
    // Desligado no admin, some só no computador, onde o sino da linha do
    // tempo faz o mesmo (TopicExtras.less); no celular é o único.
    m("div", {
      className: classList('CanelaTopicFooter-notifications', {
        'CanelaTopicFooter-notifications--optional': !forumBool('canelaTopicFooterNotifications', false)
      })
    }, m(TopicNotifications, {
      discussion: discussion
    }), m("p", {
      className: "CanelaTopicFooter-reason"
    }, notificationReason(discussion))));
  }

  /** Favorito = o marcador do primeiro post (o "bookmark" do tópico). */
  bookmarkButton(discussion) {
    if (!bookmarksEnabled()) return null;
    const bookmarked = topicIsBookmarked(discussion);
    return m(Button, {
      className: classNames('Button CanelaTopicFooter-button CanelaTopicFooter-bookmark', bookmarked && 'CanelaTopicFooter-button--active'),
      icon: bookmarked ? 'fas fa-bookmark' : 'far fa-bookmark',
      onclick: () => toggleTopicBookmark(discussion).catch(() => {})
    }, t(bookmarked ? 'topic.footer.bookmarked' : 'topic.footer.bookmark'));
  }

  /** Sinalizar o tópico = sinalizar o primeiro post, com o modal do flarum/flags. */
  flagButton(discussion) {
    if (!isExtensionEnabled('flarum-flags')) return null;
    const post = firstPost(discussion);
    if (!post || post.isHidden() || !post.attribute('canFlag')) return null;
    return m(Button, {
      className: "Button CanelaTopicFooter-button CanelaTopicFooter-flag",
      icon: "far fa-flag",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('ext:flarum/flags/forum/components/FlagPostModal'), {
        post
      })
    }, t('topic.footer.flag'));
  }

  /**
   * "Marcar não lido": a leitura volta para antes do último post lido e a
   * pessoa volta para a lista. Precisa do campo do tema
   * (extend/topic.php): o core só deixa a leitura andar para a frente.
   */
  unreadButton(discussion) {
    const lastRead = discussion.lastReadPostNumber() || 0;
    // Antes do último comentário: a leitura conta também os posts de evento
    // ("fixou a discussão"), e o `lastPostNumber` só os comentários. Voltar
    // uma posição a partir de um evento no fim deixava o tópico lido.
    const target = Math.min(lastRead, discussion.lastPostNumber() || lastRead) - 1;
    if (!app.forum.attribute('canelaMarkUnread') || lastRead < 1 || target < 0) return null;
    return m(Button, {
      className: "Button CanelaTopicFooter-button CanelaTopicFooter-unread",
      icon: "fas fa-circle",
      loading: this.markingUnread,
      onclick: () => {
        this.markingUnread = true;
        discussion.save({
          canelaMarkUnread: target
        }).then(() => {
          // A data vale contra "marcar tudo como lido" (topic/markUnread.ts).
          discussion.pushAttributes({
            lastReadPostNumber: target,
            canelaMarkedUnreadAt: new Date().toISOString()
          });
          m.route.set(app.route('index'));
        }).catch(() => {}).finally(() => {
          this.markingUnread = false;
          m.redraw();
        });
      }
    }, t('topic.footer.mark_unread'));
  }
  replyButton(discussion) {
    if (app.session.user && !discussion.canReply()) {
      return m(Button, {
        className: "Button CanelaTopicFooter-reply disabled",
        icon: "fas fa-reply",
        disabled: true
      }, app.translator.trans('core.forum.discussion_controls.cannot_reply_button'));
    }
    return m(Button, {
      className: "Button Button--primary CanelaTopicFooter-reply",
      icon: "fas fa-reply",
      onclick: () => DiscussionControls.replyAction.call(discussion, true, false).catch(() => {})
    }, t('topic.footer.reply'));
  }
}
function classNames(...names) {
  return names.filter(Boolean).join(' ');
}
flarum.reg.add('ramon-canela', 'forum/topic/components/TopicFooter', TopicFooter);